import { z } from 'zod';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { DEFAULT_PHONE_PREFIX } from '../constants';
import { optionalPattern } from './zodPatterns';
import { computeGatedFieldMessage } from '../components/Form/fieldErrorVisibility';

// local@domain.tld with an alphabetic TLD (2+ letters) or an ACE/punycode TLD
// (xn--…). Rejects numeric TLDs like "example.com2" but accepts IDNs such as
// "example.xn--p1ai". Stricter than user.schema.ts's rule (a real TLD has no
// digits); the server's URI::MailTo check remains the backstop.
export const CONTACT_EMAIL_REGEX = /^[^,;@ \r\n]+@[^,;@ \r\n]+\.(?:[A-Za-z]{2,}|xn--[A-Za-z0-9-]+)$/;

// Mobile validity via libphonenumber-js (as in user.schema.ts). Kept out of the
// zod schema: the shared @components/Form's legacy adapter dead-locks on
// `.refine` (see location.schema.ts). Bare local numbers are normalised with
// DEFAULT_PHONE_PREFIX so the "8123 4567" placeholder validates.
// Pasted numbers can carry invisible characters (WhatsApp bidi marks,
// zero-width spaces, full-width digits) that libphonenumber rejects; the
// backend strips the same set (Contact#clean_phone) before validating.
export function isValidContactMobile(value: string): boolean {
  const trimmed = value.normalize('NFKC').replace(/\p{Cf}/gu, '').trim();
  if (trimmed === '') return false;
  const normalised = trimmed.startsWith('+') ? trimmed : `${DEFAULT_PHONE_PREFIX}${trimmed}`;
  try {
    return parsePhoneNumberWithError(normalised).isValid();
  } catch {
    return false;
  }
}

// Blank passes — presence is the schema's job; a typed number must parse.
export function isContactMobileFormatValid(mobile: string): boolean {
  return mobile.trim() === '' || isValidContactMobile(mobile);
}

// Presence comes from the schema (an object error); libphone *format* is
// checked here because the shared Form adapter dead-locks on .refine.
export function computePhoneFieldMessage(
  meta: { errors: unknown[]; isBlurred?: boolean; isDirty?: boolean },
  value: string,
  submitted: boolean,
  invalidMessage: string,
): string | undefined {
  return computeGatedFieldMessage(meta, submitted, () =>
    isContactMobileFormatValid(value) ? undefined : invalidMessage,
  );
}

// Validation message keys (match i18n keys in contacts.form.errors).
// Passed in from the component so copy stays in en.yml; the static defaults
// below are fallbacks only (tests / non-i18n callers).
export type ContactValidationMessages = {
  firstNameRequired?: string;
  emailRequired?: string;
  emailInvalid?: string;
  mobileNumberRequired?: string;
};

const DEFAULT_VALIDATION_MESSAGES: Required<ContactValidationMessages> = {
  firstNameRequired: 'First name is required',
  emailRequired: 'Email is required',
  emailInvalid: 'Enter a valid email address',
  mobileNumberRequired: 'Contact number is required',
};

// Field-level rules only — deliberately NO cross-field `.refine`/`.superRefine`
// (the shared @components/Form runs the legacy zod adapter; see
// location.schema.ts for the full story). Required fields mirror
// Api::ContactsController#create's boundary rules (GRAIN-1914): first_name,
// email and mobile_number. Mobile format / email uniqueness stay server-side
// and surface as field-level 422s.
//
// `organisation` holds the picked {id, name} (the label keeps rendering in the
// dropdown trigger); it's flattened to organisation_id on the wire.
export function contactFormSchemaWithMessages(messages?: ContactValidationMessages) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };

  return z.object({
    first_name: z.string().min(1, m.firstNameRequired),
    last_name: z.string(),
    email: z.string().min(1, m.emailRequired).regex(optionalPattern(CONTACT_EMAIL_REGEX), m.emailInvalid),
    mobile_number: z.string().min(1, m.mobileNumberRequired),
    notes: z.string(),
    organisation: z.object({ id: z.number(), name: z.string() }).nullable(),
    // Create-only billing fields (GRAIN-2569). Field-level only — both-or-neither
    // and 4-to-6-digit format are enforced on submit in CreateContactForm, same reason
    // as mobile_number above.
    address: z.string().optional(),
    postal_code: z.string().optional(),
  });
}

// Static schema with default messages (for tests / non-i18n callers).
export const contactFormSchema = contactFormSchemaWithMessages();

export type ContactFormData = z.infer<typeof contactFormSchema>;
